import { useMemo, useState } from "react";
import Card from "../../../../components/card/card";
import Tabs from "../../../../components/tabs/tabs";
import { useLineups } from "../../../../hooks/use-lineups";
import { useIsViewerJourney } from "../../../viewer-feed/viewer-journey.context";
import { useOptionalBroadcastPortalContext } from "../../broadcast-portal.context";
import type { TabIds } from "../../broadcast-portal.types";
import FormationGrid from "./components/formation-grid/formation-grid";
import * as CSS from "./formations-module.styles";

type FormationsModuleProps = {
	fixtureId: number | null;
	activeTeam?: TabIds;
	homeTeam?: string;
	awayTeam?: string;
	withPadding?: boolean;
};

const FormationsModule = ({
	fixtureId,
	homeTeam,
	awayTeam,
	withPadding = true,
}: FormationsModuleProps) => {
	const portalContext = useOptionalBroadcastPortalContext();
	const [internalActiveTeam, setInternalActiveTeam] =
		useState<TabIds>("homeTeam");
	const activeTeam = portalContext?.activeTeam ?? internalActiveTeam;
	const setActiveTeam = portalContext?.setActiveTeam ?? setInternalActiveTeam;

	const isViewer = useIsViewerJourney();

	const lineups = useLineups(fixtureId);
	const teamLineup = lineups?.[activeTeam === "homeTeam" ? "home" : "away"];
	const coach = teamLineup?.coach ?? "";

	const renderFormationsContent = useMemo(() => {
		return (
			<>
				<CSS.TeamDetailsRow $isViewer={isViewer}>
					<CSS.TeamInfo>
						<CSS.InfoValue>{coach}</CSS.InfoValue>
						<CSS.InfoTitle>Manage / Head coach</CSS.InfoTitle>
					</CSS.TeamInfo>
					<CSS.TeamInfo>
						<CSS.InfoValue>{teamLineup?.formation}</CSS.InfoValue>
						<CSS.InfoTitle>Formation</CSS.InfoTitle>
					</CSS.TeamInfo>
				</CSS.TeamDetailsRow>

				{teamLineup && (
					<FormationGrid
						formation={teamLineup.formation}
						players={teamLineup.startXI}
					/>
				)}
			</>
		);
	}, [coach, teamLineup, isViewer]);

	const renderViewerPerspective = useMemo(() => {
		return (
			<div style={{ marginTop: "1rem" }}>
				<Card>{renderFormationsContent}</Card>
			</div>
		);
	}, [renderFormationsContent]);

	const renderBroadcasterPerspective = useMemo(() => {
		return renderFormationsContent;
	}, [renderFormationsContent]);

	return (
		<CSS.FormationsModule $withPadding={withPadding}>
			<div style={{ marginBottom: "1rem" }}>
				<Tabs
					activeTab={activeTeam}
					onChange={(id) => setActiveTeam(id as TabIds)}
					items={[
						{ id: "homeTeam", label: homeTeam || "Home" },
						{ id: "awayTeam", label: awayTeam || "Away" },
					]}
				/>
			</div>
			{isViewer ? renderViewerPerspective : renderBroadcasterPerspective}
		</CSS.FormationsModule>
	);
};

export default FormationsModule;
